import type { ReactNode } from 'react'
import { Card, CardBody, CardHead, CountInput, Field, RatioInput, Segmented } from './ui'
import type { IconName } from './icons'
import { ModelSelect } from './ModelSelect'

/**
 * 「输出设置」—— **全站唯一一份生成模型 + 分辨率 + 比例 + 张数 section**（2026-10-10）。
 *
 * 用户口径（输出与核对那一屏截图，圈着「生成模型」说）：「这个也是合理的……
 * 很多页面都是分散了」，连同产品信息一起点名要「合成一个组件」。
 *
 * 这四项在生成页里是同一种东西的不同副本：**生成模型**（单价乘数）、
 * **分辨率**（出图规格）、**比例**（画幅）、**张数**（数量乘数）—— 决定
 * 「花多少钱、出多大、出几张」。此前每页各写一遍 `ModelSelect + Segmented + Segmented`，
 * 改一处漏一处（典型：某页分辨率没有入口、却照样进报价）。
 *
 * ## 四项都可选 —— 各页的决策域不同
 *
 * · 详情页的「张数」是**按类型配比 / 按模块配份数**，不是简单档位 ⇒ 那一页
 *   只借本组件的模型 + 分辨率，张数留给它自己的 picker（传 `children` 或不用张数槽）；
 * · 广告图的比例**跟随画布尺寸**（`BANNER_SIZES`），所以它的比例输入框另设，
 *   走下面的 `ratio` 槽 —— 本组件只负责渲染，值怎么来由页面给；
 * · 文案页压根没有分辨率 / 模型图片档，整块不用。
 *
 * ## 张数 = 常用档位 + 自定义（2026-10-10）
 *
 * 用户口径：「这些出图数量 1-4，然后都可以自定义数量」。所以张数槽走 `CountInput`
 * （`presets` 一排档位 + 一个能自己敲的数字框），默认档位 `[1,2,3,4]`。
 * 比例槽走 `RatioInput`（下拉 + 自由输入「21:9」这类），即「自定义比例」。
 * 两者都是 `ui.tsx` 里**唯一一份实现**，本组件不重写。
 *
 * ⚠️ 模型选择只走 `ModelSelect` 一份实现（真正的「选哪个引擎」口径全在里面：
 * 通道徽标、单价、实测标注、降级提示）—— 本组件不重写一遍。
 */
export function OutputSettings({
  title = '输出设置',
  icon = 'bolt',
  headRight,
  model,
  onModelChange,
  modelKind = 'image',
  modelHasRef,
  modelLabel,
  showModel = true,
  resolution,
  onResolutionChange,
  resolutionOptions,
  resolutionLabel = '分辨率',
  resolutionHint,
  ratio,
  onRatioChange,
  ratioOptions,
  ratioLabel = '画面比例',
  count,
  onCountChange,
  countOptions = [1, 2, 3, 4],
  countLabel = '生成数量',
  countUnit = '张',
  countMax,
  order = ['model', 'resolution', 'ratio', 'count', 'extra'],
  children,
}: {
  title?: string
  icon?: IconName
  headRight?: ReactNode
  model: string
  onModelChange: (v: string) => void
  /** 图片档 / 视频档 —— 决定 ModelSelect 的候选与提示文案 */
  modelKind?: 'image' | 'video'
  /** 表单里是否已有参考素材（只影响 ModelSelect 下方提示文案） */
  modelHasRef?: boolean
  modelLabel?: string
  showModel?: boolean
  resolution?: string
  /** 不传则**不渲染分辨率槽**（该页分辨率固定 / 无入口） */
  onResolutionChange?: (v: string) => void
  resolutionOptions?: { value: string; label: ReactNode }[]
  resolutionLabel?: string
  resolutionHint?: ReactNode
  ratio?: string
  /** 不传则**不渲染比例槽**（该页比例固定 / 无入口） */
  onRatioChange?: (v: string) => void
  /** 比例下拉的常用候选（自由输入的值会被补进去显示） */
  ratioOptions?: string[]
  ratioLabel?: string
  count?: number
  /** 不传则**不渲染张数槽**（张数由页面自己的 picker 决定） */
  onCountChange?: (n: number) => void
  /** 张数的常用档位 —— 默认 `[1,2,3,4]`；用户想要别的直接敲旁边的数字框 */
  countOptions?: number[]
  countLabel?: string
  countUnit?: string
  /** 自定义数字框的上限（防误输用，默认 99） */
  countMax?: number
  /**
   * 五个槽位的排列顺序。默认「模型 → 分辨率 → 比例 → 张数 → 追加项」。
   *
   * ⚠️ 各页原有顺序并不相同（社媒图是「张数 → 比例 → 分辨率 → 模型」），
   * 所以顺序做成入参而不是写死 —— 统一的是**控件与口径**，不是把每页的排版
   * 也一并抹平（那会平白无故地改动用户没抱怨过的页面）。
   */
  order?: ('model' | 'resolution' | 'ratio' | 'count' | 'extra')[]
  /** 页面特有的追加项（说明文字、画面文字占比…），落在 `extra` 位置 */
  children?: ReactNode
}) {
  const blocks: Record<'model' | 'resolution' | 'ratio' | 'count' | 'extra', ReactNode> = {
    model: showModel ? (
      <ModelSelect
        key="model"
        kind={modelKind}
        value={model}
        onChange={onModelChange}
        hasRef={modelHasRef}
        label={modelLabel}
      />
    ) : null,
    resolution: onResolutionChange ? (
      <Field key="resolution" label={resolutionLabel} hint={resolutionHint}>
        <Segmented value={resolution ?? ''} onChange={onResolutionChange} options={resolutionOptions ?? []} />
      </Field>
    ) : null,
    ratio: onRatioChange ? (
      <Field key="ratio" label={ratioLabel}>
        <RatioInput value={ratio ?? ''} onChange={onRatioChange} options={ratioOptions ?? []} />
      </Field>
    ) : null,
    count: onCountChange ? (
      <Field key="count" label={countLabel}>
        <CountInput
          value={count ?? countOptions[0]}
          onChange={onCountChange}
          presets={countOptions}
          unit={countUnit}
          max={countMax}
        />
      </Field>
    ) : null,
    extra: children ?? null,
  }

  return (
    <Card>
      <CardHead title={title} icon={icon} right={headRight} />
      <CardBody>{order.map((k) => blocks[k])}</CardBody>
    </Card>
  )
}
